import { ScorecardSourceSelector } from "../Inputs/ScorecardSourceSelector";
import {
  COLUMN_AGGREGATE_TYPE_LABELS,
  columnAggregateCanScore,
  columnAggregateUsesExpectedColumn,
} from "../../../criterionConfig";
import type { ColumnAggregateScoreMode, ColumnAggregateType } from "../../../criterionConfig";
import { columnAggregateCopy } from "../EditCriterionView.constants";
import { Field } from "../Layout/Field";

import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const SUMMARY_TYPES: ColumnAggregateType[] = [
  "average_value",
  "most_frequent_value",
  "min_value",
  "max_value",
  "percentile_value",
];
const EXPECTED_COLUMN_TYPES: ColumnAggregateType[] = [
  "classification_report",
  "mean_absolute_error",
  "root_mean_squared_error",
];

interface ColumnAggregateFieldsProps {
  columnAggregateType: ColumnAggregateType;
  onColumnAggregateTypeChange: (type: ColumnAggregateType) => void;
  columnAggregateScoreMode: ColumnAggregateScoreMode;
  onColumnAggregateScoreModeChange: (mode: ColumnAggregateScoreMode) => void;
  columnAggregateScoreMax: string;
  onColumnAggregateScoreMaxChange: (value: string) => void;
  labelColumnId: string;
  onLabelColumnIdChange: (value: string) => void;
  expectedColumnId: string;
  onExpectedColumnIdChange: (value: string) => void;
  percentile: string;
  onPercentileChange: (value: string) => void;
  sourceColumnId: string;
  sourceColumnOptions: { id: string; title: string }[];
  scoreContributionLabel: string;
  scoreContributionHelper: string;
}

export const ColumnAggregateFields = ({
  columnAggregateType,
  onColumnAggregateTypeChange,
  columnAggregateScoreMode,
  onColumnAggregateScoreModeChange,
  columnAggregateScoreMax,
  onColumnAggregateScoreMaxChange,
  labelColumnId,
  onLabelColumnIdChange,
  expectedColumnId,
  onExpectedColumnIdChange,
  percentile,
  onPercentileChange,
  sourceColumnId,
  sourceColumnOptions,
  scoreContributionLabel,
  scoreContributionHelper,
}: ColumnAggregateFieldsProps) => (
  <>
    <Field label="Evaluate by">
      <Select
        value={columnAggregateType}
        onValueChange={value => onColumnAggregateTypeChange(value as ColumnAggregateType)}
      >
        <SelectTrigger>
          <SelectValue placeholder="Choose how to evaluate" />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectLabel>Summarize the column</SelectLabel>
            {SUMMARY_TYPES.map(type => (
              <SelectItem key={type} value={type}>
                {COLUMN_AGGREGATE_TYPE_LABELS[type]}
              </SelectItem>
            ))}
          </SelectGroup>
          <SelectGroup>
            <SelectLabel>Compare with expected values</SelectLabel>
            {EXPECTED_COLUMN_TYPES.map(type => (
              <SelectItem key={type} value={type}>
                {COLUMN_AGGREGATE_TYPE_LABELS[type]}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
      <p className="text-xs text-muted-foreground">
        {columnAggregateCopy[columnAggregateType].helper}
      </p>
    </Field>

    {columnAggregateUsesExpectedColumn(columnAggregateType) && (
      <ScorecardSourceSelector
        value={expectedColumnId}
        onValueChange={onExpectedColumnIdChange}
        placeholder="Expected values"
        options={sourceColumnOptions.filter(column => column.id !== sourceColumnId)}
        name="Expected column"
      />
    )}

    {columnAggregateType === "percentile_value" && (
      <Field label="Percentile">
        <Input
          value={percentile}
          onChange={event => onPercentileChange(event.target.value)}
          inputMode="decimal"
          placeholder="95"
        />
      </Field>
    )}

    {columnAggregateCanScore(columnAggregateType) && (
      <Field label="Score contribution">
        <Select
          value={columnAggregateScoreMode}
          onValueChange={value =>
            onColumnAggregateScoreModeChange(value as ColumnAggregateScoreMode)
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Choose score behavior" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="value">Show result only</SelectItem>
            <SelectItem value="score">{scoreContributionLabel}</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">{scoreContributionHelper}</p>
      </Field>
    )}

    {columnAggregateType === "average_value" && columnAggregateScoreMode === "score" && (
      <Field label="Maximum value">
        <Input
          value={columnAggregateScoreMax}
          onChange={event => onColumnAggregateScoreMaxChange(event.target.value)}
          inputMode="decimal"
          placeholder="1"
        />
        <p className="text-xs text-muted-foreground">Example: use 5 for a 1–5 rating column.</p>
      </Field>
    )}

    {(columnAggregateType === "min_value" || columnAggregateType === "max_value") && (
      <div className="space-y-2">
        <ScorecardSourceSelector
          value={labelColumnId}
          onValueChange={onLabelColumnIdChange}
          placeholder="Winner label"
          options={sourceColumnOptions.filter(column => column.id !== sourceColumnId)}
          name="Show winner as"
        />
        <p className="text-xs text-muted-foreground">
          Optional. Use this when the lowest/highest row should display a model or label.
        </p>
      </div>
    )}
  </>
);
